import React, { FC, ReactElement, useEffect } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Grid, darken } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { clsx } from 'clsx';
// Components
import Text from '../text';
import Space from '../layout/space';
import RadiusButton from '../buttons/radiusButton';
import Separator from '../layout/separator';
import SvgIcon from '../icons/SvgIcon';
import TextCenterButton from '../buttons/textCenterButton';
import LCAnimation from '../icons/LCAnimation';
import EditHistoryTipsAndRating from '../../navigation/routes/tripHistory/editHistoryTipsAndRating';
// Modules
import { actions as currentBookingActions } from '../../core/modules/currentBooking';
// Queries
// Resources
import { Ride, Status } from '../../core/apiTypes';
import { stringsRes } from '../../resources/strings';
import { callButtonBackground } from '../../resources/style/theme';
import {
    convertStatusForAnimation,
    convertStatusForSoon,
    convertStatusForSoonSub,
    getVehicleInfos,
    isBookingLoading,
} from '../../utils/business';
import { addTimeToDate } from '../../utils/date';
import { getSuffixWithNumber } from '../../utils/strings';
import { paths } from '../../navigation/routes/paths';
import { Animation, IconAxel } from '../../resources/images';
import { getUser } from '../../localStorageAPI';
import { client } from '../../core/services/helpers';
import { isWebView } from '../../utils/webview';
import { DRIVER_ETA_TIME } from '../../utils/constants';
import { WebLocation, useBreakpoints } from '../../customHooks/useBreakpoints';
import { ModalType } from '../../core/modules/modals';
import { ModalSize } from '../modals/createModalHOC';
import useGlobalStyles from '../hook/useGlobalStyles';

interface ReservationTrackingProps {
    reservation: Ride;
    resetAll: () => any;
    openModal?: (modalType: ModalType, props: any, id?: string) => any;
}

const ReservationTracking: FC<ReservationTrackingProps> = (props): ReactElement => {
    const { reservation, resetAll, openModal } = props;
    const { status, driver_tracking: driverTracking, vehicle, driver } = reservation || {};

    const classes = useStyles();
    const globalClasses = useGlobalStyles();
    const navigate = useNavigate();
    const user = getUser();
    const isWebview = isWebView();
    const { isMobileSize } = useBreakpoints(WebLocation.FLOW);

    let infos = null;
    let secondText = null;

    //= =====================================//
    //= ============== Effect ===============//
    //= =====================================//

    useEffect(() => {
        if (reservation.status === Status.FINISHED) {
            // Only show the rating modal if the rider is the connected user
            if (reservation.riders?.[0].id === user?.id) {
                openModal(ModalType.EDITION, {
                    title: stringsRes.tripHistory.tipsAndRating.title,
                    floatingClose: isMobileSize ? 'topRight' : 'none',
                    ...(isMobileSize && { modalPosition: 'bottom' }),
                    children: <EditHistoryTipsAndRating />,
                    modalSize: isMobileSize ? ModalSize.lg : ModalSize.xl,
                    modalStyle: clsx(
                        globalClasses.modalRating,
                        //     isWebView() && classes.statusBarTop,
                        isMobileSize && globalClasses.modalBottomRating,
                    ),
                    labelConfirm: stringsRes.common.add,
                    ride: reservation,
                    navigate,
                });
            }
            goToHome();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservation]);

    //= ===========================================//
    //= ============== HomeTracking ===============//
    //= ===========================================//
    const isHomeTrackingMobile = document.location.pathname.startsWith(paths.homeTrackingMobile.replace(':id', ''));

    const goToHome = () => {
        navigate(paths.home);
    };

    // const goToRideDetails = () => {
    //     navigate(paths.reservationsDetail.replace(':id', reservation.id));
    // };

    // const onGoToSupport = (): void => {
    //     navigateTo(navigate, `${paths.support}?ride_reference=${reservation.id}`);
    // };

    const onCreateNewRide = (): void => {
        resetAll();
        client.invalidateQueries('historySoon');
        goToHome();
    };

    //= ======================================//
    //= ============== Renders ===============//
    //= ======================================//

    const renderIcon = (): ReactElement => {
        if (isBookingLoading(status)) {
            return <LCAnimation animation={Animation.Loader} roundedBackground />;
        }
        return <LCAnimation animation={convertStatusForAnimation(status)} />;
    };

    const driverEta = driverTracking?.eta;
    const customText = status === Status.PASSENGER_ON_BOARD && driverEta;

    const ratingTipProps = { ride: reservation, navigate, onRequestClose: null };

    // Tracking page size vs normal - see if we want to change
    const InfoText = isHomeTrackingMobile ? Text.ShortMediumText16 : Text.ShortMediumText16;
    const SecondText = isHomeTrackingMobile ? Text.Description14 : Text.Description14;

    switch (status) {
        case Status.CREATED:
        case Status.READY_TO_DISPATCH:
        case Status.PROCESSING:
            return (
                <>
                    <div className={classes.centerContainer}>
                        {renderIcon()}
                        <div className={classes.centerDetails}>
                            <InfoText text={stringsRes.bookingSoon.status.processing} />
                            <SecondText text={stringsRes.bookingSoon.status.processingSub} />
                        </div>
                    </div>
                </>
            );
        case Status.DISPATCH_TIMEOUT:
            // Fake Status - 10 minutes driver
            secondText = ` ${getSuffixWithNumber(DRIVER_ETA_TIME, stringsRes.time.min, stringsRes.time.mins)}`;
            break;
        case Status.RECEIVED:
            break;
        case Status.ACCEPTED:
        case Status.AT_PICKUP:
        case Status.PASSENGER_ON_BOARD:
            if (!isHomeTrackingMobile) {
                infos = (
                    <>
                        <Space value={3} />
                        <Separator height={1} />
                        <Space value={3} />

                        <Grid container>
                            <Grid item xs={8} className={classes.centerDetails}>
                                <Text.H35 text={vehicle?.registration_number} />
                                <Space />
                                <Text.Description14 text={getVehicleInfos(vehicle, true)} customColor="grey" />
                                {driver && (
                                    <Text.Description14
                                        text={`${stringsRes.booking.driver.name} ${driver?.firstname}`}
                                        customColor="grey"
                                    />
                                )}
                            </Grid>
                            <Grid item xs={4} className={classes.buttonContainer}>
                                <a
                                    aria-label={stringsRes.profile.phone}
                                    href={`tel:${isWebview ? '//' : ''}0183777598`}
                                >
                                    {isMobileSize ? (
                                        <RadiusButton
                                            title=""
                                            iconElement={
                                                <SvgIcon
                                                    icon={IconAxel.Phone}
                                                    size="medium"
                                                    overwritePathStroke
                                                    stroke="white"
                                                />
                                            }
                                            className={classes.roundedButton}
                                            noSpace
                                        />
                                    ) : (
                                        <RadiusButton
                                            title="01 83 77 75 98"
                                            iconElement={<SvgIcon icon={IconAxel.Phone} size="small" />}
                                            className={classes.phoneButton}
                                        />
                                    )}
                                </a>
                            </Grid>
                        </Grid>
                    </>
                );
            }
            if (status === Status.PASSENGER_ON_BOARD && driverEta) {
                secondText = `${addTimeToDate(driverEta, undefined, 's').format(stringsRes.time.hourFormat)}`;
            } else if (status === Status.ACCEPTED && driverEta) {
                secondText = ` ${getSuffixWithNumber(
                    Math.floor(driverEta / 60),
                    stringsRes.time.min,
                    stringsRes.time.mins,
                )}`;
            } else {
                secondText = null;
            }
            break;
        case Status.FINISHED:
            return (
                <>
                    {isHomeTrackingMobile && (
                        <>
                            <Text.H15 text={stringsRes.tripHistory.tipsAndRating.title} />
                            <EditHistoryTipsAndRating {...(ratingTipProps as any)} />
                        </>
                    )}
                </>
            );
        case Status.CANCELLATION_NO_CHARGE:
        case Status.CANCELLATION_WITH_CHARGE:
            return (
                <div className={classes.reservationTrackingCancelContainer}>
                    <Text.Description14 text={stringsRes.booking.status.all_driver_busy} customColor="red" />
                    <Space value={3} />
                    <Separator height={1} />
                    <Space value={3} />
                    <TextCenterButton
                        onClick={onCreateNewRide}
                        buttonSize="small"
                        title={stringsRes.booking.bookNewRide}
                        customColor="lightBlue"
                    />
                </div>
            );
        default:
            return null;
    }

    if (status === Status.DISPATCH_TIMEOUT && isHomeTrackingMobile) {
        // Fake Status - 10 minutes driver
        return (
            <>
                <div className={classes.centerContainer}>
                    {renderIcon()}
                    <div className={classes.centerDetails}>
                        <InfoText
                            text={stringsRes.bookingSoon.status.fakeIncoming}
                            secondText={getSuffixWithNumber(DRIVER_ETA_TIME, stringsRes.time.min, stringsRes.time.mins)}
                            className={classes.bolder}
                        />
                        <SecondText text={stringsRes.bookingSoon.status.processingSub} />
                    </div>
                </div>
            </>
        );
    }

    return (
        <div
            className={clsx(
                classes.reservationTrackingContainer,
                isHomeTrackingMobile && classes.reservationTrackingContainerNoBorder,
                'LCreservationTrackingContainer',
            )}
        >
            <div className={classes.centerContainer}>
                {renderIcon()}
                <div className={classes.centerDetails}>
                    <InfoText
                        text={`${convertStatusForSoon(status, driverEta)}`}
                        secondText={customText ? null : secondText}
                        className={classes.bolder}
                    />
                    <SecondText
                        text={convertStatusForSoonSub(status, driverEta)}
                        secondText={customText ? secondText : null}
                        className={classes.bolderSecond}
                    />
                </div>
            </div>
            {infos}
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        reservationTrackingContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            // alignItems: 'center',

            /* Auto layout */
            padding: '24px 32px',

            /* Background/Grey Beton */
            borderWidth: '0px 1px 1px 1px',
            borderStyle: 'solid',
            borderColor: theme.palette.colors.background.menuSeparator,
            borderRadius: '0px 0px 10px 10px',
        },
        reservationTrackingContainerNoBorder: {
            border: 'none',
            padding: 'unset',
        },
        centerContainer: {
            // alignSelf: 'center',
            display: 'flex',
            gap: 12,
            alignItems: 'center',
        },
        centerDetails: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'start',
        },
        buttonContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            alignSelf: 'center',
            columnGap: 8,
        },
        roundedButton: {
            padding: 14,
            minWidth: 'unset',
            // The phone icon is hardcoded white and `secondary.main` turns near-white in dark mode, which is
            // how this button ended up white on white. Deep blue keeps a dark ground under the icon there.
            backgroundColor: callButtonBackground(theme),
            border: 'unset',
            '&:hover': {
                backgroundColor: darken(callButtonBackground(theme), 0.05),
                border: 'unset',
            },
        },
        phoneButton: {
            border: 'unset',
            borderRadius: 10,
            color: theme.palette.primary.main,
            padding: '16px 24px',
            backgroundColor: theme.palette.colors.background.primaryElement,
            '&:hover': {
                backgroundColor: darken(theme.palette.colors.background.primaryElement, 0.05),
                border: 'unset',
            },
        },
        reservationTrackingCancelContainer: {
            marginLeft: theme.spacing(2),
            marginRight: theme.spacing(2),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
        },
        bolder: {
            fontWeight: '700 !important',
        },
        bolderSecond: {
            '& > span': {
                fontWeight: '700 !important',
            },
        },
    }),
);
const mapStateToProps = () => ({});

const mapDispatchToProps = {
    resetAll: currentBookingActions.resetAll,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReservationTracking);
